<template>
  <div class="content">
    <div class="header">
      <h2>{{ pageTitle }}</h2>
    </div>
    <div class="content-body table-wrapper">
      <div v-if="bomId">
        <Row :gutter="32">
          <Col span="6">
            <Row :gutter="32" class="margin-bottom-10">
              <Col span="10">
                <Strong>{{ $t('bom_tool.version.version') }}:</Strong>
              </Col>
              <Col span="14">
                <BomVersionSelect :bomId="bomId" :versionNumber.sync="version"></BomVersionSelect>
              </Col>
            </Row>
            <Row :gutter="32" class="margin-bottom-10">
              <Col span="10">
                <Strong>{{ $t('bom_tool.version.compare_source') }}:</Strong>
              </Col>
              <Col span="14">
                <Select v-model="selectedVersionSource"
                        :placeholder="$t('form.select_version_source')"
                        filterable
                        transfer>
                  <Option v-for="v in versionsSource" :value="v.value" :key="v.value"
                          :label="v.label">
                    {{v.value}}
                  </Option>
                </Select>
              </Col>
            </Row>
            <Row :gutter="32">
              <Col span="10">
                <Strong>{{ $t('bom_tool.version.compare_version') }}:</Strong>
              </Col>
              <Col span="14">
                <BomVersionSelect v-if="selectedVersionSource === 'BOM'" :bomId="bomId" :versionNumber.sync="versionToCompare"></BomVersionSelect>
                <ErpVersionSelect v-else :bomId="bomId"  :versionNumber.sync="versionToCompare"></ErpVersionSelect>
              </Col>
            </Row>
          </Col>
          <Button type="primary" @click="handleCompareBomVersions(bomId, version, versionToCompare)">
            {{ $t('bom_tool.version.button_compare') }}
          </Button>
        </Row>
        <br>
        <vxe-grid
          v-if="version"
          border
          resizable
          stripe
          :loading="loading"
          :columns="columns"
          ref="bomVersion"
          column-min-width="50"
          :scroll-y="{gt: -1}"
          size="mini"
          :data="tableData"
          row-id="id"
          :filterConfig="{showIcon: true}">
          <template v-slot:compare="{row, rowIndex, $rowIndex, column, columnIndex, $columnIndex, _columnIndex}">
            <div>
              <div v-if="!columnVersionHasDiff(column, row)">{{_.get(row, column.property)}}</div>
              <div v-else>
                <div style="background:#e6ffed;">
                  <span :title="'Ver. ' + version">{{_.get(row,  column.property)}}</span>
                </div>
                <hr></hr>
                <div style="background:#ffeef0;">
                  <span :title="'Ver. ' + versionToCompare">{{_.get(row, 'version_compare.' + column.property)}}</span>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:assembly="{row, rowIndex, $rowIndex, column, columnIndex, $columnIndex, _columnIndex}">
            <div v-if="row.assembly_bom_id">
              <router-link :to="{name: 'menu.bom.versions', params: {bomId: row.assembly_bom_id, version: row.version}}">
                <Icon type="ios-link"></Icon>
              </router-link>
            </div>
          </template>
        </vxe-grid>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'
import 'xe-utils'
import VXETable from 'vxe-table'
import 'vxe-table/lib/index.css'
import BomVersionSelect from './bom-versions-select'
import ErpVersionSelect from './erp-versions-select'
Vue.use(VXETable)
export default {
  name: 'BomVersionSnapshotView',
  components: {
    BomVersionSelect,
    ErpVersionSelect
  },
  computed: {
    pageTitle () {
      document.title = 'BOM Diff Tool - ' + this.bomName
      return document.title
    }
  },
  data() {
    return {
      loading: false,
      bomVersions: {},
      bomName: null,
      version: null,
      versionToCompare: null,
      bomId: null,
      tableData: [],
      tableToolbar: {
        perfect: true,
        zoom: true,
        custom: true
      },
      columns: [
        {
          type: 'seq',
          width: 90,
          showHeaderOverflow: true,
          align: 'left'
          // treeNode: true
        },
        {
          showHeaderOverflow: false,
          showOverflow: false,
          field: 'change_flag',
          title: 'State',
          width: 90,
          filters: [
            { label: 'New', value: 'New' },
            { label: 'Changed', value: 'Changed' },
            { label: 'Deleted', value: 'Deleted' },
            { label: 'Not Changed', value: null }
          ],
          filterMultiple: true
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'primary_manufacturer_part.part_identity.part_number',
          title: 'Assembly BOM',
          align: 'right',
          minWidth: 20,
          slots: {
            default: 'assembly'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'primary_manufacturer_part.part_identity.part_number',
          title: 'Part Number',
          align: 'left',
          minWidth: 110,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'version',
          title: 'Version',
          align: 'left',
          width: 55,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'line_quantity',
          title: 'Qty',
          align: 'right',
          width: 50,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'designator',
          title: 'Designator',
          align: 'left',
          minWidth: 400,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'specification',
          title: 'Specification',
          align: 'left',
          minWidth: 300,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'mpn',
          title: 'MPN',
          align: 'left',
          minWidth: 150,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'brand',
          title: 'Brand',
          align: 'left',
          minWidth: 100,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'name',
          title: 'Name',
          minWidth: 150,
          slots: {
            default: 'compare'
          }
        },
        {
          showHeaderOverflow: true,
          showOverflow: false,
          field: 'chinese_name',
          title: 'Chinese name',
          minWidth: 150,
          slots: {
            default: 'compare'
          }
        }
      ],
      selectedVersionSource: 'BOM',
      versionsSource: [
        { label: 'BOM', value: 'BOM' },
        { label: 'ERP', value: 'ERP' }
      ]
    }
  },
  watch: {
    '$route.params.bomId': function (val) {
      this.selectedVersionSource = 'BOM'
      this.bomVersions = {}
      this.loading = false
      this.bomId = val
      this.version = this.$route.params.version
      this.versionToCompare = null
      this.handleCompareBomVersions(this.bomId, this.version)
    }
  },
  mounted() {
    this.handleCompareBomVersions(this.$route.params.bomId,
      this.$route.params.version,
      this.$route.params.version_compare)
  },
  methods: {
    ...mapActions([
      'getBomMain',
      'getBomItems',
      'getErpBomByPartNumber'
    ]),
    async handleCompareBomVersions (bomId, version, versionToCompare) {
      this.bomId = bomId
      this.version = version
      this.versionToCompare = versionToCompare
      this.tableData = []

      if (this.selectedVersionSource === 'BOM') {
        await this.handleLoadBomVersion(this.bomId, this.version)
        if (this.versionToCompare) {
          await this.handleLoadBomVersion(this.bomId, this.versionToCompare)
        }
        this.tableData = await this.generateTableData(this.bomId, this.version, this.versionToCompare)
      } else {
        await this.handleLoadBomVersion(this.bomId, this.version)
        if (this.versionToCompare) {
          await this.getErpBomByPartNumber(this.versionToCompare).then((res) => {
            if (!this.bomVersions[bomId]) {
              this.bomVersions[bomId] = {}
            }
            this.bomVersions[bomId][this.versionToCompare] = res
          })
          this.tableData = await this.generateErpTableData(this.bomId, this.version, this.versionToCompare)
        }
      }
      this.loading = false
    },
    async handleLoadBomVersion(bomId, version) {
      this.loading = true
      if (bomId && version) {
        let result = await this.getBomMain({
          id: bomId,
          version: version
        }).then((res) => {
          if (!this.bomVersions[bomId]) {
            this.bomVersions[bomId] = {}
          }
          this.bomVersions[bomId][version] = res
          if (bomId === this.bomId && version === this.version) {
            this.bomName = res.name
          }
        })
        return result
      }
      return null
    },
    async generateTableData(bomId, version, versionToCompare = null) {
      let bomItems = await this.getBomItems({ id: bomId, version: version }).then(resp => {
        return _.map(resp, (item) => { return _.mapValues(item, this.convertNull) })
      })

      if (versionToCompare) {
        let bomItemsToCompare = await this.getBomItems({ id: bomId, version: versionToCompare }).then(resp => {
          return _.map(resp, (item) => { return _.mapValues(item, this.convertNull) })
        })

        _.each(bomItems, bomItem => {
          let findCompare = _.find(bomItemsToCompare, (compare) => {
            return bomItem.id === compare.id
          })
          let needFields = ['primary_manufacturer_part.part_identity.part_number', 'version', 'line_quantity', 'designator']
          bomItem.is_equale = _.isEqual(_.pick(bomItem, needFields), _.pick(findCompare, needFields))
          bomItem.version_compare = findCompare
          bomItem.change_flag = !findCompare ? 'New' : (bomItem.is_equale ? null : 'Changed')
        })
        let deletedBomItems = _.map(_.differenceBy(bomItemsToCompare, bomItems, 'id'), (deletedItem) => {
          return {
            is_equale: false,
            is_deleted: true,
            change_flag: 'Deleted',
            version_compare: _.clone(deletedItem)
          }
        })
        bomItems = _.concat(bomItems, deletedBomItems)
      }
      return bomItems
    },
    convertNull (val) {
      return !val ? '' : val
    },
    async generateErpTableData(bomId, version, versionToCompare = null) {
      let bomItems = await this.getBomItems({ id: bomId, version: version }).then(resp => _.map(resp, (item) => _.mapValues(item, this.convertNull)))

      if (versionToCompare) {
        let bomItemsToCompare = _.map(this.bomVersions[bomId][versionToCompare].bom_items, (item) => {
          return _.mapValues(item, this.convertNull)
        })
        _.each(bomItems, bomItem => {
          let findCompare = _.find(bomItemsToCompare, (compare) => {
            // return bomItem.part_number === compare.part_number
            return (bomItem.designator !== '' && _.find(bomItemsToCompare, (item) => { return item.designator === bomItem.designator })) ? bomItem.designator === compare.designator : bomItem.part_number === compare.part_number
          })
          let needFields = ['designator', 'part_number', 'version', 'line_quantity']
          bomItem.is_equale = _.isEqual(_.pick(bomItem, needFields), _.pick(findCompare, needFields))
          bomItem.version_compare = findCompare
          bomItem.change_flag = !findCompare ? 'New' : (bomItem.is_equale ? null : 'Changed')
        })
        let deletedBomItems = _.map(_.differenceBy(bomItemsToCompare, bomItems, 'part_number'), (deletedItem) => {
          return {
            is_equale: false,
            is_deleted: true,
            change_flag: 'Deleted',
            version_compare: _.clone(deletedItem)
          }
        })
        bomItems = _.concat(bomItems, deletedBomItems)
      }
      return bomItems
    },
    columnVersionHasDiff(column, row) {
      if (!this.versionToCompare) {
        return false
      }
      return _.get(row, column.property) !== _.get(row, 'version_compare.' + column.property)
    }
  }
}
</script>
